import React from 'react';
import { GovernanceMetricInfo } from './GovernanceMetricInfo';
import { BandedMetric, bandSentence, toneForMetric } from './bandVisuals.utils';
import { SentimentThumb } from './SentimentThumb';
import { SkeletonBlock } from './BrandPresenceSkeleton';

export function GovernanceAnalyticsKpiCard({
  title,
  value,
  valueSuffix,
  adornment,
  subtitle,
  loading,
  info,
  onClick,
  bandMetric,
  bandValue,
  children,
}: {
  title: string;
  value: string;
  /** Muted text right after the number, such as the "/ 25" of a count out of a total. */
  valueSuffix?: string;
  /** A badge standing next to the number, such as the severity of the value shown. */
  adornment?: React.ReactNode;
  subtitle?: React.ReactNode;
  loading?: boolean;
  /**
   * Which band scale this card's number is read against, when it is read against one at all.
   *
   * Only score metrics carry bands. A share — "42% of answers mentioned you" — has no favourable
   * reading on its own, so a card showing one passes nothing here and gets no icon.
   */
  bandMetric?: BandedMetric;
  /** The raw 0-1 value behind `value`, which is a preformatted string. */
  bandValue?: number;
  /** One-sentence plain-language definition shown behind a small `?` explainer. */
  info?: string;
  /** When set, the whole card becomes an interactive drill-down. */
  onClick?: () => void;
  /** A small visual under the number, such as a meter of the value against its scale. */
  children?: React.ReactNode;
}) {
  const interactive = !!onClick;
  const tone = bandMetric ? toneForMetric(bandMetric, bandValue) : undefined;

  return (
    <div
      className={`group relative h-full overflow-hidden rounded-2xl bg-white p-5 shadow-card ring-1 ring-slate-200/70 transition-all duration-200 ${
        interactive
          ? 'cursor-pointer hover:-translate-y-0.5 hover:shadow-card-hover hover:ring-primary/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50'
          : ''
      }`}
      onClick={onClick}
      role={interactive ? 'button' : undefined}
      tabIndex={interactive ? 0 : undefined}
      aria-busy={loading || undefined}
      onKeyDown={
        interactive
          ? (event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
    >
      <p className="mb-3 flex items-center gap-1 font-eyebrow text-[0.6875rem] font-medium uppercase tracking-[0.14em] text-slate-500">
        {title}
        {info ? <GovernanceMetricInfo info={info} /> : null}
      </p>
      {loading ? (
        <div className="space-y-2.5">
          <SkeletonBlock className="h-9 w-24 rounded-lg" />
          <SkeletonBlock className="h-3 w-32" />
        </div>
      ) : (
        <div className="motion-safe:animate-bp-fade-in">
          <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
            <span className="text-[2rem] leading-none font-bold tracking-tight text-slate-900 tabular-nums">
              {value}
              {valueSuffix ? <span className="ml-1 text-xl font-semibold text-slate-500">{valueSuffix}</span> : null}
            </span>
            {/* The band this number falls in, from the shared thresholds — never a boundary of its own,
                so moving a boundary moves this card with every other surface. */}
            {bandMetric && tone ? (
              <span title={bandSentence(bandMetric, tone)}>
                <SentimentThumb tone={tone} metric={bandMetric} />
              </span>
            ) : null}
            {adornment}
          </div>
          {subtitle ? <p className="mt-2 mb-0 text-sm text-slate-500">{subtitle}</p> : null}
          {children ? <div className="mt-4">{children}</div> : null}
        </div>
      )}
    </div>
  );
}

/**
 * A slim meter under a KPI: the share of the scale the value fills, drawn in the brand gradient
 * unless the caller colours it by what the value means.
 */
export function KpiMeter({
  fraction,
  label,
  fillClassName = 'bg-brand-gradient',
}: {
  fraction: number;
  label: string;
  fillClassName?: string;
}) {
  const percent = Math.round(Math.max(0, Math.min(1, fraction)) * 100);

  return (
    <div
      className="h-1.5 w-full overflow-hidden rounded-full bg-slate-100"
      role="meter"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-valuenow={percent}
    >
      {/* The fill's width is the value itself, known only at runtime. */}
      <span
        className={`block h-full rounded-full transition-[width] duration-700 ease-out ${fillClassName}`}
        style={{ width: `${percent}%` }}
      />
    </div>
  );
}
